import { HUB_BASE_URL } from "@constants/env";
import { getAuthToken } from "@helpers/auth-provider";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";

const getContentType = (fileName: string) => {
	if (fileName.endsWith(".webm")) {
		return "audio/webm";
	}
	if (fileName.endsWith(".m4a")) {
		return "audio/mp4";
	}
	return "application/octet-stream";
};

const uploadRecording = async (
	input: Blob | string,
	fileName: string,
): Promise<string | null> => {
	try {
		const token = await getAuthToken();
		if (!token) {
			captureErrorToSentry(
				new Error("Error uploading recording: missing auth token"),
				{ fileName },
			);
			return null;
		}

		const presignRes = await fetch(
			`${HUB_BASE_URL}${HUB_ENDPOINTS.RECORDINGS_PRESIGN}`,
			{
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ fileName }),
			},
		);
		if (!presignRes.ok) {
			const responseText = await presignRes.text().catch(() => "");
			captureErrorToSentry(
				new Error("Error generating recording presigned URL"),
				{
					fileName,
					responseStatus: presignRes.status,
					error: serializeErrorForSentry(responseText),
				},
			);
			return null;
		}
		const { signedUrl, publicUrl } = (await presignRes.json()) as {
			signedUrl: string;
			publicUrl: string;
		};

		const blob =
			typeof input === "string"
				? await fetch(input).then((r) => r.blob())
				: input;

		const putRes = await fetch(signedUrl, {
			method: "PUT",
			headers: {
				"Content-Type": getContentType(fileName),
			},
			body: blob,
		});
		if (!putRes.ok) {
			const responseText = await putRes.text().catch(() => "");
			captureErrorToSentry(
				new Error("Error uploading recording to signed URL"),
				{
					fileName,
					responseStatus: putRes.status,
					error: serializeErrorForSentry(responseText),
				},
			);
			return null;
		}

		return publicUrl;
	} catch (error) {
		captureErrorToSentry(new Error("Error uploading recording"), {
			fileName,
			error: serializeErrorForSentry(error),
		});
		return null;
	}
};

export { uploadRecording };
